import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import DateTimePicker from "../../components/DateTimePicker";
import FilePreviewModal from "../../components/FilePreviewModal";
import pdfIcon from "../../assets/svg/pdf-icon.svg";
import {
  generateRenewalPoaPdf,
  getRenewalPoaContext,
  RENEWAL_APPLICANT_TYPES,
  saveRenewalPoa,
  type RenewalPoaContext,
} from "../../api/renewal";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";
import { toLocalYmd } from "../../utils/filingApiDates";

export const RENEWAL_PREPARATION_OF_POA_FORM_ID =
  "renewal-preparation-of-power-of-attorney-form";

const LABEL_STYLE = "text-xs font-normal leading-5 flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;

function formatApplicantTypeLabel(value: string | null | undefined): string {
  if (!value) return "";
  const found = RENEWAL_APPLICANT_TYPES.find((o) => o.value === value);
  return found?.label ?? value;
}

function FetchedDetailsPanel({
  rows,
  className = "rounded-[8px]",
}: {
  rows: Array<{ label: string; value: string; multiline?: boolean }>;
  className?: string;
}) {
  return (
    <div
      className={`bg-[#F7F7F7] p-4 md:p-5 flex flex-col gap-3 w-full ${className}`}
      style={{
        fontFamily: "Inter, sans-serif",
        fontFeatureSettings: "'ss01' on, 'cv01' on",
      }}
    >
      {rows.map(({ label, value, multiline }) => (
        <div
          key={label}
          className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4"
        >
          <span className="text-xs font-normal leading-5 text-[#6D6D6D] md:w-[220px] md:shrink-0">
            {label}
          </span>
          <span
            className={`text-xs font-normal leading-5 break-words ${
              multiline ? "whitespace-pre-wrap" : ""
            } ${value ? "text-[#222222]" : "text-[#6D6D6D]"}`}
          >
            {value || "—"}
          </span>
        </div>
      ))}
    </div>
  );
}

function EditableTextarea({
  label,
  value,
  onChange,
  placeholder = "Text Here",
  invalid = false,
  required = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  invalid?: boolean;
  required?: boolean;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col gap-1 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label
        className={LABEL_STYLE}
        style={{ ...LABEL_FEATURES, color: "#222222" }}
      >
        {label}
        {required && <span className="text-[#E7000B] ml-0.5">*</span>}
      </label>
      <div
        className={`box-border w-full bg-white border rounded-[6px] focus-within:ring-1 focus-within:ring-[#222] ${
          invalid ? "border-[#E7000B] focus-within:ring-[#E7000B]" : "border-[#EEEEEE]"
        }`}
      >
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className={`w-full bg-transparent focus:outline-none resize-y px-[10px] py-2 text-[12px] md:text-xs leading-5 ${
            value ? "text-[#222222]" : "text-[#6D6D6D]"
          }`}
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        />
      </div>
    </div>
  );
}

type RenewalPreparationOfPowerOfAttorneyViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function RenewalPreparationOfPowerOfAttorneyView({
  leadId,
  onSuccess,
}: RenewalPreparationOfPowerOfAttorneyViewProps) {
  const [context, setContext] = useState<RenewalPoaContext | null>(null);
  const [dateOfPowerOfAttorney, setDateOfPowerOfAttorney] =
    useState<Date | null>(null);
  const [descriptionOfApplicant, setDescriptionOfApplicant] = useState("");

  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewFilename, setPreviewFilename] = useState<string>("");

  const autoSaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const isDateMissing = !dateOfPowerOfAttorney;

  const applyContext = (data: RenewalPoaContext) => {
    setContext(data);
    setDateOfPowerOfAttorney(
      data.dateOfPowerOfAttorney ? new Date(data.dateOfPowerOfAttorney) : null
    );
    setDescriptionOfApplicant(data.descriptionOfApplicant ?? "");
  };

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getRenewalPoaContext(leadId)
      .then((data) => {
        if (cancelled) return;
        applyContext(data);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Power of Attorney data"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    return () => {
      if (autoSaveDebounceRef.current)
        clearTimeout(autoSaveDebounceRef.current);
    };
  }, []);

  const buildPayload = () => ({
    dateOfPowerOfAttorney: dateOfPowerOfAttorney
      ? toLocalYmd(dateOfPowerOfAttorney)
      : undefined,
    descriptionOfApplicant: descriptionOfApplicant || undefined,
  });

  // Auto-save preview-relevant fields so generated PDFs stay in sync.
  useEffect(() => {
    if (!leadId || loading || !context) return;
    if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
    autoSaveDebounceRef.current = setTimeout(() => {
      saveRenewalPoa(leadId, buildPayload())
        .then((data) => {
          setContext(data);
        })
        .catch(() => {
          // Auto-save errors are non-blocking; the explicit submit will surface real issues.
        });
    }, 600);
    return () => {
      if (autoSaveDebounceRef.current)
        clearTimeout(autoSaveDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, dateOfPowerOfAttorney, descriptionOfApplicant]);

  const validateBeforeAction = (): boolean => {
    if (isDateMissing) {
      setShowValidationErrors(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (!context) {
      toast.error("Power of Attorney data is not ready yet.");
      return;
    }
    if (!validateBeforeAction()) return;
    if (autoSaveDebounceRef.current) {
      clearTimeout(autoSaveDebounceRef.current);
      autoSaveDebounceRef.current = null;
    }
    setSaving(true);
    try {
      const data = await saveRenewalPoa(leadId, buildPayload());
      applyContext(data);
      toast.success("Power of Attorney saved successfully.");
      setShowValidationErrors(false);
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId) return;
    if (!validateBeforeAction()) return;
    setGenerating(true);
    try {
      await saveRenewalPoa(leadId, buildPayload());
      await generateRenewalPoaPdf(leadId);
      const fresh = await getRenewalPoaContext(leadId);
      setContext(fresh);
      toast.success("Power of Attorney PDF generated successfully.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (): Promise<void> => {
    const pdf = context?.pdf;
    if (!pdf?.url) {
      toast.error("Generate PDF first before downloading.");
      return;
    }
    setDownloadingPdf(true);
    try {
      const result = await downloadFileFromUrlWithFallback(
        pdf.url,
        pdf.filename ?? "power-of-attorney.pdf"
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to download PDF"
      );
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePreview = async () => {
    if (!leadId) return;
    if (!validateBeforeAction()) return;
    if (context?.pdf?.url && !context.pdfIsStale) {
      setPreviewFilename(context.pdf.filename || "power-of-attorney.pdf");
      setPreviewPdfUrl(context.pdf.url);
      return;
    }
    setPreviewing(true);
    try {
      await saveRenewalPoa(leadId, buildPayload());
      await generateRenewalPoaPdf(leadId);
      const fresh = await getRenewalPoaContext(leadId);
      setContext(fresh);
      if (fresh.pdf?.url) {
        setPreviewFilename(fresh.pdf.filename || "power-of-attorney.pdf");
        setPreviewPdfUrl(fresh.pdf.url);
      } else {
        toast.error("Failed to generate PDF for preview.");
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate preview"
      );
    } finally {
      setPreviewing(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Loading Power of Attorney data...
        </p>
      </div>
    );
  }

  if (!context) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          No Power of Attorney data found. Please complete the previous renewal
          steps first.
        </p>
      </div>
    );
  }

  const pdfIsStale = context.pdfIsStale;

  return (
    <form
      id={RENEWAL_PREPARATION_OF_POA_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Details (fetched from previous step) */}
      <h4 className="text-xs font-medium leading-5 text-[#6D6D6D]">
        Details (from previous step)
      </h4>

      <FetchedDetailsPanel
        rows={[
          { label: "Applicant", value: context.applicant ?? "" },
          { label: "Applicant Address", value: context.applicantAddress ?? "" },
          {
            label: "Applicant Type",
            value: formatApplicantTypeLabel(context.applicantType),
          },
          { label: "Signatory's Name", value: context.signatoryName ?? "" },
          {
            label: "Signatory's Designation",
            value: context.signatoryDesignation ?? "",
          },
          { label: "Mark", value: context.mark ?? "" },
        ]}
      />

      {/* Editable: Date of Power of Attorney */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <div
          className="flex flex-1 min-w-0 flex-col gap-1 w-full"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <label
            className={LABEL_STYLE}
            style={{ ...LABEL_FEATURES, color: "#222222" }}
          >
            Date of Power of Attorney
            <span className="text-[#E7000B] ml-0.5">*</span>
          </label>
          <DateTimePicker
            value={dateOfPowerOfAttorney}
            onChange={setDateOfPowerOfAttorney}
            showTime={false}
            buttonClassName={`min-h-[44px] md:min-h-[36px] h-9 rounded-[6px] text-[12px] md:text-xs ${
              showValidationErrors && isDateMissing
                ? "border-[#E7000B] focus:ring-[#E7000B]"
                : ""
            }`}
          />
        </div>
      </div>

      <EditableTextarea
        label="Description of Applicant"
        value={descriptionOfApplicant}
        onChange={setDescriptionOfApplicant}
      />

      {/* PDF actions */}
      <div className="flex flex-col gap-4 md:gap-5 w-full p-3 md:p-4 rounded-[10px] border border-[#EEEEEE] bg-[#FAFAFA]">
        <div className="flex flex-col gap-2.5 w-full">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="text-[12px] md:text-xs font-medium text-[#222222]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Power of Attorney
            </span>
            {context.pdf && pdfIsStale && (
              <span
                className="text-[11px] leading-4 text-[#A86A00] bg-[#FFF8EC] border border-[#FAD9A6] rounded px-1.5 py-0.5"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Inputs changed — regenerate to refresh
              </span>
            )}
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
            <button
              type="button"
              onClick={handleGeneratePdf}
              disabled={generating}
              className="flex min-h-[44px] sm:h-9 items-center justify-center gap-2 rounded-[6px] border border-[#EEEEEE] bg-white px-4 py-2.5 sm:py-2 text-[12px] md:text-xs font-medium leading-5 text-[#222222] hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <img src={pdfIcon} alt="" className="h-[22px] w-[22px] shrink-0" />
              {generating
                ? "Generating..."
                : context.pdf
                ? "Regenerate PDF"
                : "Generate PDF"}
            </button>
            <button
              type="button"
              onClick={handlePreview}
              disabled={previewing}
              className="min-h-[44px] sm:h-9 rounded-[6px] border border-[#EEEEEE] bg-white px-4 py-2.5 sm:py-2 text-center text-[12px] md:text-xs font-medium leading-5 text-[#222222] hover:bg-[#F7F7F7] disabled:opacity-60 touch-manipulation"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {previewing ? "Generating..." : "Preview PDF"}
            </button>
            <button
              type="button"
              onClick={() => {
                void handleDownload();
              }}
              disabled={downloadingPdf || !context.pdf}
              className="min-h-[44px] sm:h-9 rounded-[6px] bg-[#222222] px-4 py-2.5 sm:py-2 text-center text-[12px] md:text-xs font-medium leading-5 text-white hover:bg-[#333333] touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {downloadingPdf ? "Downloading…" : "Download"}
            </button>
          </div>
        </div>
      </div>

      <FilePreviewModal
        open={!!previewPdfUrl}
        onClose={() => setPreviewPdfUrl(null)}
        name={previewFilename || "Power of Attorney"}
        url={previewPdfUrl}
        kind="pdf"
      />
    </form>
  );
}
